import type { z } from "zod";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import {
  DaySchema,
  OrderShiftSchema,
  OrderShiftSchemaWithId,
  ShiftSchema,
  ShiftSchemaWithId,
} from "@repo/validators";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { OrderShift, Shift } from "~/providers/shift-provider";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { usePermissionManager } from "./use-permission-manager";

export const useShiftManager = () => {
  const { t } = useTranslation("common");
  const utils = api.useUtils();

  const { showDialog } = useAlertStore();

  const {
    handleShiftUpdate,
    handleShiftDelete,
    handleOrderShiftUpdate,
    handleOrderShiftDelete,
  } = useShiftUpdates();

  const { hasPermission } = usePermissionManager();

  const { mutate: createShift } = api.shift.create.useMutation({
    async onSuccess(id) {
      toast.success(capitalize(t("create_item_success", { item: t("shift") })));
      await handleShiftUpdate(id);
    },
    onError() {
      toast.error(
        capitalize(t("create_item_error", { item: t("Shift.shift") })),
      );
    },
  });

  const { mutate: createOrderShiftMutation } =
    api.shift.createOrderShift.useMutation({
      async onSuccess(id) {
        toast.success(
          capitalize(t("create_item_success", { item: t("shift") })),
        );
        await handleOrderShiftUpdate(id);
      },
      onError() {
        toast.error(
          capitalize(t("create_item_error", { item: t("Shift.shift") })),
        );
      },
    });

  const { mutate: updateOrderShiftMutation } =
    api.shift.updateOrderShift.useMutation({
      async onSuccess(_, { id }) {
        toast.success(
          capitalize(t("update_item_success", { item: t("shift") })),
        );
        await handleOrderShiftUpdate(id);
      },
      onError() {
        toast.error(
          capitalize(t("update_item_error", { item: t("Shift.shift") })),
        );
      },
    });

  const { mutate: deleteOrderShiftMutation } =
    api.shift.deleteOrderShift.useMutation({
      onSuccess(_, id) {
        toast.success(
          capitalize(t("delete_item_success", { item: t("Shift.shift") })),
        );
        handleOrderShiftDelete(id);
      },
      onError() {
        toast.error(
          capitalize(t("delete_item_fail", { item: t("Shift.shift") })),
        );
      },
    });

  const { mutate: updateShift } = api.shift.update.useMutation({
    async onSuccess(_, { id }) {
      toast.success(capitalize(t("update_item_success", { item: t("shift") })));
      await handleShiftUpdate(id);
    },
    onError() {
      toast.error(
        capitalize(t("update_item_error", { item: t("Shift.shift") })),
      );
    },
  });

  const { mutate: closeDay } = api.shift.closeDay.useMutation({
    onSuccess(_, { startDate }) {
      toast.success(capitalize(t("update_item_success", { item: t("day") })));
      void utils.shift.getDay.invalidate(startDate);
    },
  });

  const { mutate: updateDay } = api.shift.updateDay.useMutation({
    onSuccess(_, { startDate }) {
      toast.success(capitalize(t("update_item_success", { item: t("day") })));
      void utils.shift.getDay.invalidate(startDate);
    },
  });

  const { mutate: deleteShift } = api.shift.deleteShift.useMutation({
    onSuccess(_, id) {
      toast.success(
        capitalize(t("delete_item_success", { item: t("Shift.shift") })),
      );
      handleShiftDelete(id);
    },
    onError() {
      toast.error(
        capitalize(t("delete_item_fail", { item: t("Shift.shift") })),
      );
    },
  });

  const createShiftHandler = async (
    data: z.infer<ReturnType<typeof ShiftSchema>>,
  ) => {
    const shift = ShiftSchema().safeParse(data);

    if (!shift.success) {
      console.error(shift.error);
      return false;
    }

    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
      label: t("Permissions.create_shift_label"),
    });

    if (!permission) return false;

    createShift(shift.data);

    return true;
  };

  const updateShiftHandler = async (
    data: z.infer<ReturnType<typeof ShiftSchemaWithId>>,
  ) => {
    const shift = ShiftSchemaWithId().safeParse(data);

    if (!shift.success) {
      console.error(shift.error);
      return false;
    }

    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
      label: t("Permissions.update_shift_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      updateShift(shift.data, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const deleteShiftHandler = async (shift: Shift) => {
    const result = await showDialog({
      title: t("Shift.before_delete_title"),
      message: t("Shift.before_delete_message"),
      variant: "destructive",
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });
    return new Promise<boolean>((resolve) => {
      if (result === "cancel") {
        return resolve(false);
      }
      deleteShift(shift.id, {
        onSuccess() {
          return resolve(true);
        },
        onError() {
          return resolve(false);
        },
      });
    });
  };

  const closeDayHandler = async (
    day: z.infer<ReturnType<typeof DaySchema>>,
  ) => {
    const parsed = DaySchema().safeParse(day);
    return new Promise<boolean>((resolve) => {
      if (parsed.success === false) {
        console.error(parsed.error);
        return resolve(false);
      }

      if (day.closed === false) {
        return resolve(true);
      }
      closeDay(
        {
          ...day,
        },
        {
          onSuccess() {
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  const updateDayHandler = (day: z.infer<ReturnType<typeof DaySchema>>) => {
    const parsed = DaySchema().safeParse(day);
    return new Promise<boolean>((resolve) => {
      if (parsed.success === false) {
        console.error(parsed.error);
        return resolve(false);
      }
      updateDay(
        { ...day },
        {
          onSuccess() {
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  const createOrderShift = async (
    data: z.infer<ReturnType<typeof OrderShiftSchema>>,
  ) => {
    const orderShift = OrderShiftSchema().safeParse(data);

    if (!orderShift.success) {
      console.error(orderShift.error);
      return false;
    }

    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
      label: t("Permissions.create_shift_label"),
    });

    if (!permission) return false;

    createOrderShiftMutation(orderShift.data);

    return true;
  };

  const updateOrderShift = async (
    data: z.infer<ReturnType<typeof OrderShiftSchemaWithId>>,
  ) => {
    const orderShift = OrderShiftSchemaWithId().safeParse(data);

    if (!orderShift.success) {
      console.error(orderShift.error);
      return false;
    }

    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
      label: t("Permissions.update_shift_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      updateOrderShiftMutation(orderShift.data, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const deleteOrderShift = async (orderShift: OrderShift) => {
    const result = await showDialog({
      title: t("Shift.before_delete_title"),
      message: t("Shift.before_delete_message"),
      variant: "destructive",
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });
    return new Promise<boolean>((resolve) => {
      if (result === "cancel") {
        return resolve(false);
      }
      deleteOrderShiftMutation(orderShift.id, {
        onSuccess() {
          return resolve(true);
        },
        onError() {
          return resolve(false);
        },
      });
    });
  };

  return {
    createOrderShift,
    updateOrderShift,
    deleteOrderShift,
    createShift: createShiftHandler,
    updateShift: updateShiftHandler,
    closeDay: closeDayHandler,
    updateDay: updateDayHandler,
    deleteShift: deleteShiftHandler,
  };
};

export const useShiftUpdates = () => {
  const utils = api.useUtils();

  const handleShiftUpdate = async (shiftId: string) => {
    const result = await utils.shift.getById.fetch(shiftId);

    void utils.shift.getAll.setData(undefined, (old) => {
      if (!old) return old;
      return [...old.filter((p) => p.id !== result.id), result];
    });
  };

  const handleShiftDelete = (shiftId: string) => {
    void utils.shift.getAll.setData(undefined, (old) => {
      if (!old) return old;
      return old.filter((p) => p.id !== shiftId);
    });
  };

  const handleOrderShiftUpdate = async (shiftId: string) => {
    const result = await utils.shift.getById.fetch(shiftId);

    void utils.shift.getOrderShifts.setData(undefined, (old) => {
      if (!old) return old;
      return [...old.filter((p) => p.id !== result.id), result];
    });
  };

  const handleOrderShiftDelete = (shiftId: string) => {
    void utils.shift.getOrderShifts.setData(undefined, (old) => {
      if (!old) return old;
      return old.filter((p) => p.id !== shiftId);
    });
  };

  return {
    handleShiftUpdate,
    handleShiftDelete,
    handleOrderShiftUpdate,
    handleOrderShiftDelete,
  };
};
